/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";

type IdGetter<T> = (obj: T) => string;

export type AuswahlService<T> = {
    aendereAuswahl: (element: T, ausgewaehlt: boolean) => void;
    aendereAuswahlFuerAlle: (ausgewaehlt: boolean) => void;
    aendereAuswahlFuerAlleHandler: (e: unknown, ausgewaehlt: boolean) => void;
    aendereAuswahlHandler: (element: T) => (e: unknown, ausgewaehlt: boolean) => void;
    alleAbwaehlen: () => void;
    alleAusgewaehlt: boolean;
    ausgewaehlteElemente: T[];
    elementeAuswaehlen: (elemente: T[]) => void;
    istAusgewaehlt: (element: T) => boolean;
    istAusgewaehltId: (id: string) => boolean;
    nichtsAusgewaehlt: boolean;
    unbestimmteAuswahl: boolean;
    alleZuBedingungAusgewaehlt: (bedingung: (element: T) => boolean) => boolean;
    unbestimmteAuswahlZuBedingung: (bedingung: (element: T) => boolean) => boolean;
    aendereAuswahlFuerAlleZuBedingung: (bedingung: (element: T) => boolean, ausgewaehlt: boolean) => void;
}

export default function useAuswahl<T>(elemente: T[], getId: IdGetter<T>, intialAuswahlIds?: string[]): AuswahlService<T> {
    const [auswahlIds, setAuswahlIds] = React.useState<string[]>(intialAuswahlIds ?? []);

    const ausgewaehlteElemente = elemente.filter(element => auswahlIds.includes(getId(element)));
    const nichtsAusgewaehlt = ausgewaehlteElemente.length === 0;
    const alleAusgewaehlt = !nichtsAusgewaehlt && ausgewaehlteElemente.length === elemente.length;
    const unbestimmteAuswahl = !nichtsAusgewaehlt && !alleAusgewaehlt;

    const istAusgewaehltId = (id: string) => auswahlIds.includes(id);

    const istAusgewaehlt = (element: T) => istAusgewaehltId(getId(element));

    const abwaehlen = (element: T) =>
        setAuswahlIds(prev => prev.filter(id => getId(element) !== id));

    const auswaehlen = (element: T) =>
        setAuswahlIds(prev => Array.from(new Set([...prev, getId(element)])));

    const aendereAuswahl = (element: T, ausgewaehlt: boolean) =>
        ausgewaehlt ? auswaehlen(element) : abwaehlen(element);

    const elementeAuswaehlen = (elemente: T[]) =>
        setAuswahlIds(elemente.map(getId));

    const aendereAuswahlHandler = (element: T) =>
        (e: unknown, ausgewaehlt: boolean) => aendereAuswahl(element, ausgewaehlt);

    const aendereAuswahlFuerAlle = (ausgewaehlt: boolean) =>
        ausgewaehlt ? setAuswahlIds(elemente.map(getId)) : setAuswahlIds([]);

    const aendereAuswahlFuerAlleHandler = (e: unknown, ausgewaehlt: boolean) =>
        aendereAuswahlFuerAlle(ausgewaehlt);

    const alleAbwaehlen = () => aendereAuswahlFuerAlle(false);

    const elementIdsString = JSON.stringify(elemente.map(getId));
    React.useEffect(() => {
        const elementIds = JSON.parse(elementIdsString);
        setAuswahlIds(prev => prev.filter(auswahlId => elementIds.includes(auswahlId)))
    }, [elementIdsString])

    const alleZuBedingungAusgewaehlt = (bedingung: (element: T) => boolean) => {
        return elemente.filter(bedingung).every(e => istAusgewaehlt(e));
    }

    const unbestimmteAuswahlZuBedingung = (bedingung: (element: T) => boolean) => {
        const ausgewaehlteElementeZuBedingung = ausgewaehlteElemente.filter(bedingung);
        return elemente.filter(bedingung).length !== ausgewaehlteElementeZuBedingung.length && ausgewaehlteElementeZuBedingung.length > 0;
    }

    const aendereAuswahlFuerAlleZuBedingung = (bedingung: (element: T) => boolean, ausgewaehlt: boolean) => {
        elemente.filter(bedingung).forEach(element =>{
            aendereAuswahl(element, ausgewaehlt)
        });
    }

    return {
        aendereAuswahl,
        aendereAuswahlFuerAlle,
        aendereAuswahlFuerAlleHandler,
        aendereAuswahlHandler,
        alleAbwaehlen,
        alleAusgewaehlt,
        ausgewaehlteElemente,
        elementeAuswaehlen,
        istAusgewaehlt,
        istAusgewaehltId,
        nichtsAusgewaehlt,
        unbestimmteAuswahl,
        alleZuBedingungAusgewaehlt,
        unbestimmteAuswahlZuBedingung,
        aendereAuswahlFuerAlleZuBedingung,
    };
}